import React, { useState, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';

import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../_commons/emotionCache';

// import useFilters from './hooks/filters';

// import { journeyTypeToLongValue } from '../../../helpers/utils';
import {
  MobileBody,
  MobileFilterDialog,
  MobileHeader
} from '.././../../../components/lab/MobileFilterView';
import FareItemOriginDestination from '../../../items/_commons/atoms/FareItemOriginDestination';

import FocusLock from 'react-focus-lock';

//-------------------------------------
// Basic components here
//-------------------------------------
import Button from '../../../elements/Button';
import ImageHeader from '../../../elements/ImageHeader';
import Box from '../../../elements/Box';
import Icon from '../../../elements/Icon';
import Text from '../../../elements/Text';
import PassengerFilter from '../../../controls/SecondaryFilters/PassengerFilter';
import InlineFilters from './InlineFilter';
import { useFilters } from '../hook/useFilters';
import { strFormat } from '../../../../helpers/utils';
import FareItemPrice from '../../../items/_commons/atoms/FareItemPrice';
import FareItemJourneyType from '../../../items/_commons/atoms/FareItemJourneyType';
import FareItemTravelClass from '../../../items/_commons/atoms/FareItemTravelClass';
import FareItemCallToAction from '../../../items/_commons/atoms/FareItemCallToAction';
import Separator from '../../../items/_commons/atoms/Separator';
import { ButtonTerms } from './ButtonTerms';
import { publish } from '../../../../_integrations/everymundo/tracking/pubsub';
import { journeyTypeToLongValue } from '../../../../helpers/utils';
import { isSSR } from '@front10/utils';
import { notifyExitFullMode } from './../../../containers/_commons/navigation';
import { formatDate } from '../../../../_integrations/everymundo/tracking/helpers';

function getPubSub() {
  if (window && window.EM && window.EM.utils && window.EM.utils.PubSub) {
    return window.EM.utils.PubSub;
  }
  return undefined;
}

const BookingPopup = (props) => {
  const {
    bookingContainer = {},
    bookingModalDialog = {},
    bookingModalContent = {},
    bookingModalHeader = {},
    bookingTitleSettings = {},
    bodySettings = {},
    backgroundImageSettings = {},
    itemOriginDestination,
    format = 'DD MMM YYYY',
    iconClose = {},
    buttonClose = {},
    isMobile = false,
    containerSettings,
    containerFiltersSetting,
    filters,
    showFilters,
    showSecondaryFilters,
    secondaryFilters,
    prePopulation,
    travelBetweenLabel,
    minMaxStayLabel,
    buttonTermsSettings,
    bookingDescriptionSettings,
    isIframe = false,
    headingStyle,
    itemSettings: {
      fareBodyItem,
      fareFooterItem,
      showPrice,
      fareItemPrice,
      showJourneyType,
      showTravelClass,
      fareItemJourneyTypeTravelClass,
      fareItemJourneyType,
      separatorJourneyTypeProps,
      fareItemTravelClass,
      showCallToAction,
      fareItemCallToAction,
      fareItemCallToActionContainer,
      tabIndexCallToAction
    },
    datalayerTracking = false
  } = props;
  const { translate, theme, direction } = React.useContext(Context);

  const [isVisible, setVisible] = useState(false);
  const [isVisibleTerms, setVisibleTerms] = useState(false);
  const [popupData, setPopupData] = useState({});

  const [className, style] = useStyles(bookingContainer, { direction, theme });
  const minStayNumber = popupData.minStay ? parseInt(popupData.minStay.slice(0, -1)) : 0;
  const maxStayNumber = popupData.maxStay ? parseInt(popupData.maxStay.slice(0, -1)) : 0;
  const minStayString = popupData.minStay ? popupData.minStay.slice(-1) : 'd';
  const maxStayString = popupData.maxStay ? popupData.maxStay.slice(-1) : 'd';

  const closeButtonRef = React.useRef(null);

  const open = useCallback(() => {
    setVisible(true);
    setTimeout(() => closeButtonRef.current?.focus(), 500);
  }, []);
  const close = useCallback(() => {
    setVisible(false);
    if (isIframe && !isSSR() && !isMobile) {
      notifyExitFullMode();
    }
  }, [isIframe, isMobile]);

  const { dispatch, state, ...filtersState } = useFilters({
    filters,
    secondaryFilters,
    prePopulation,
    popupData,
    datalayerTracking
  });

  useEffect(() => {
    const pubSub = getPubSub?.();
    let token = pubSub?.subscribe('em.fare.popup.open', (_event, data) => {
      const { departureDate, returnDate, passengers } = data;

      let options = [...passengers.options];
      if (secondaryFilters?.passengerSettings?.maxAdult) {
        options[0].maxCount = secondaryFilters.passengerSettings.maxAdult;
      }
      if (secondaryFilters?.passengerSettings?.maxChild) {
        options[1].maxCount = secondaryFilters.passengerSettings.maxChild;
      }
      if (secondaryFilters?.passengerSettings?.maxInfant) {
        options[2].maxCount = secondaryFilters.passengerSettings.maxInfant;
      }

      const newData = {
        ...data,
        passengers: {
          ...data.passengers,
          options: options
        },
        dateRange: {
          start: dayjs(departureDate).toDate(),
          end: dayjs(returnDate).toDate()
        }
      };

      setPopupData(newData);

      filtersState.passengers.dispatch({
        type: 'VALUE_SET',
        value: {
          count: prePopulation.passengers ? prePopulation.passengers : [1, 0, 0]
        }
      });
      dispatch({
        type: 'SET_ALL',
        payload: {
          passengers: prePopulation.passengers ? prePopulation.passengers : [1, 0, 0],
          dateRange: newData.dateRange
        }
      });
      open();
      const journeyTypeMap = journeyTypeToLongValue();
      const popupData = {
        currencyCode: data.currencyCode,
        callToActionLabel: 'Fare-Card',
        departureDate: data.departureDate,
        returnDate: data.returnDate,
        fareClass: data.farenetTravelClass.toUpperCase(),
        journeyType: journeyTypeMap[data.journeyType],
        flexibleDates: false,
        promoCode: 'n/a',
        siteEdition: data.siteEdition,
        totalPrice: data.formattedTotalPrice,
        totalPriceUSD: 'n/a',
        passengerCount:
          prePopulation?.passengers && Array.isArray(prePopulation.passengers)
            ? prePopulation.passengers.reduce((total, i) => {
                return total + i;
              })
            : 1,
        destinationAirportIataCode: data.destination,
        originAirportIataCode: data.origin,
        useMiles: false
      };

      publish('em.fare.popup.routeStatus', popupData);
    });
    return () => {
      pubSub?.unsubscribe(token);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const travelBetween = (
    <Box {...travelBetweenLabel}>
      {`${translate('cmp-travel-breaking-deals-booking-travel-between', 'Travel Between')} ${dayjs(
        popupData.departureStart
      ).format(format)} - ${dayjs(popupData.departureEnd).format(format)}`}
    </Box>
  );

  const minMaxStay = () => {
    let minStay = '';
    let maxStay = '';
    if (minStayString === 'd') {
      if (minStayNumber > 1) {
        minStay = strFormat(translate('cmp-travel-breaking-deals-booking-days', `{0} days`), [
          minStayNumber
        ]);
      } else {
        minStay = strFormat(translate('cmp-travel-breaking-deals-booking-day', `{0} day`), [
          minStayNumber
        ]);
      }
    } else if (minStayString === 'm') {
      if (minStayNumber > 1) {
        minStay = strFormat(translate('cmp-travel-breaking-deals-booking-months', `{0} months`), [
          minStayNumber
        ]);
      } else {
        minStay = strFormat(translate('cmp-travel-breaking-deals-booking-month', `{0} month`), [
          minStayNumber
        ]);
      }
    }

    if (maxStayString === 'd') {
      if (minStayNumber > 1) {
        maxStay = strFormat(translate('cmp-travel-breaking-deals-booking-days', `{0} days`), [
          maxStayNumber
        ]);
      } else {
        maxStay = strFormat(translate('cmp-travel-breaking-deals-booking-day', `{0} day`), [
          maxStayNumber
        ]);
      }
    } else if (maxStayString === 'm') {
      if (maxStayNumber > 1) {
        maxStay = strFormat(translate('cmp-travel-breaking-deals-booking-months', `{0} months`), [
          maxStayNumber
        ]);
      } else {
        maxStay = strFormat(translate('cmp-travel-breaking-deals-booking-month', `{0} month`), [
          maxStayNumber
        ]);
      }
    }

    return (
      <Box {...minMaxStayLabel}>
        {`${translate('cmp-travel-breaking-deals-booking-min', 'min')} ${minStay} ${translate(
          'cmp-travel-breaking-deals-booking-max',
          'max'
        )} ${maxStay}`}
      </Box>
    );
  };

  const filterPros = {
    ...filters,
    availableFilters: [
      popupData.journeyType === 'rt' ? 'SingleDateRangePicker' : 'SingleDatePicker'
    ],
    dateRangeSettings:
      popupData.journeyType === 'rt'
        ? {
            ...filters.dateRangeSettings,
            childrenLeft:
              dayjs(popupData.departureStart).isValid() && dayjs(popupData.departureEnd).isValid()
                ? travelBetween
                : null,
            childrenRight: minMaxStay(),
            maxTripDuration:
              popupData.journeyType === 'rt'
                ? maxStayString === 'm'
                  ? maxStayNumber * 30
                  : maxStayNumber
                : undefined,
            minTripDuration:
              popupData.journeyType === 'rt'
                ? minStayString === 'm'
                  ? minStayNumber * 30
                  : minStayNumber
                : undefined,
            minDate: popupData.departureStart,
            maxDate: popupData.departureEnd
            // departure: {
            //   ...filters.dateRangeSettings.departure,
            //   end:
            //     maxStayString && maxStayNumber
            //       ? maxStayString === 'm'
            //         ? maxStayNumber * 30
            //         : maxStayNumber
            //       : filters.dateRangeSettings.departure.end
            // }
          }
        : {
            ...filters.dateRangeSettings,
            children:
              dayjs(popupData.departureStart).isValid() && dayjs(popupData.departureEnd).isValid()
                ? travelBetween
                : null,
            minDate: popupData.departureStart,
            maxDate: popupData.departureEnd
          }
  };

  const body = (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={theme.designTokens ?? undefined}>
        <Box style={{ height: '100%', display: 'flex', 'flex-direction': 'column' }}>
          <Box {...bookingDescriptionSettings}>
            {`${translate(
              `cmp-travel-class--${popupData.farenetTravelClass?.toLowerCase().replace('_', '-')}`,
              `cmp-travel-class--${popupData.farenetTravelClass?.toLowerCase().replace('_', '-')}`
            )} ${translate(`cmp-travel-breaking-deals-booking-class`, 'Class')} -  ${translate(
              `cmp-travel-breaking-deals-booking-book-until`,
              'Book until'
            )} ${dayjs(popupData.bookUntilDate).format(format)}`}
          </Box>

          <ImageHeader
            focusable={false}
            image={popupData.destinationImage}
            {...backgroundImageSettings}
          />
          <Box {...bodySettings}>
            <Box>
              <FareItemOriginDestination key="od" {...itemOriginDestination} {...popupData} />
              {showSecondaryFilters && (
                <Box className="fr-secondary-filters" display="flex" {...containerSettings}>
                  <Box display="inline-block" {...containerFiltersSetting}>
                    <Box
                      key="Passenger"
                      marginRight="8px"
                      marginBottom="12px"
                      floatLeft
                      {...secondaryFilters.passengerColumnSettings}
                    >
                      <PassengerFilter
                        {...secondaryFilters.passengerSettings}
                        {...filtersState.passengers}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              {showFilters && <InlineFilters {...filterPros} {...filtersState} />}
              <Box
                display="flex"
                flexDirection="column"
                {...fareBodyItem}
                className="fr-booking-fare-body-item"
              >
                {showPrice && fareItemPrice && <FareItemPrice {...fareItemPrice} {...popupData} />}
                {(showJourneyType || showTravelClass) && (
                  <Box {...fareItemJourneyTypeTravelClass}>
                    {showJourneyType && (
                      <FareItemJourneyType {...fareItemJourneyType} {...popupData} />
                    )}
                    {showTravelClass && fareItemTravelClass && (
                      <Separator {...separatorJourneyTypeProps} floatLeft />
                    )}
                    {showTravelClass && (
                      <FareItemTravelClass {...fareItemTravelClass} {...popupData} />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
            <Box {...fareFooterItem}>
              {showCallToAction && fareItemCallToAction && (
                <Box {...fareItemCallToActionContainer}>
                  <FareItemCallToAction
                    {...fareItemCallToAction}
                    {...popupData}
                    tabIndex={tabIndexCallToAction}
                    onClick={() => {
                      filtersState.dateRange?.ref?.current?.validate();
                      let isValid = false;
                      setTimeout(() => {
                        isValid = filtersState.dateRange?.ref?.current?.valid();
                        if (
                          (popupData.journeyType === 'rt' &&
                            state.dateRange.start &&
                            state.dateRange.end &&
                            isValid) ||
                          (popupData.journeyType === 'ow' && state.dateRange.start && isValid)
                        ) {
                          dispatch(
                            {
                              type: 'FARE_CLICK',
                              payload: {
                                fare: {
                                  ...popupData,
                                  departureDate: formatDate(state.dateRange.start),
                                  returnDate: formatDate(state.dateRange.end)
                                },
                                callToActionLabel: 'Book now'
                              }
                            },
                            state
                          );
                        }
                      }, 500);
                    }}
                  />
                </Box>
              )}
              {!isMobile && (
                <ButtonTerms
                  onClick={() => setVisibleTerms(true)}
                  onClose={() => setVisibleTerms(false)}
                  terms={popupData.terms}
                  isMobile={isMobile}
                  {...buttonTermsSettings}
                />
              )}
            </Box>
            {isMobile && (
              <ButtonTerms
                onClick={() => setVisibleTerms(true)}
                onClose={() => setVisibleTerms(false)}
                terms={popupData.terms}
                isMobile={isMobile}
                {...buttonTermsSettings}
              />
            )}
          </Box>
        </Box>
      </ThemeProvider>
    </EmotionCache>
  );

  return (
    <>
      <FocusLock autoFocus returnFocus>
        {!isMobile && isVisible && popupData ? (
          <Box
            className={`${className} bookingContainer`}
            style={{
              style,
              opacity: isVisibleTerms ? 0 : null,
              'z-index': isVisibleTerms ? 0 : null
            }}
          >
            <Box className="fr-modal-dialog" {...bookingModalDialog}>
              <Box className="fr-modal-content" {...bookingModalContent}>
                <Box
                  className="fr-modal-header "
                  {...bookingModalHeader}
                  //   ariaHidden={closeButtonFocus}
                >
                  <Text {...bookingTitleSettings}>{popupData.name}</Text>
                  <Button
                    ariaLabel="Close"
                    {...buttonClose}
                    onClick={() => close()}
                    focusReferences={(e) => (closeButtonRef.current = e)}
                  >
                    <Icon icon="close" {...iconClose} />
                  </Button>
                </Box>
                {body}
              </Box>
              {/* {footer} */}
            </Box>
          </Box>
        ) : (
          isVisible && (
            <EmotionCache direction={direction}>
              <ThemeProvider theme={theme.designTokens}>
                <MobileFilterDialog label={popupData.name}>
                  <MobileHeader
                    title={popupData.name}
                    onBackButtonPress={() => {
                      close();
                    }}
                    headingStyle={headingStyle}
                  />
                  <MobileBody sx={{ mt: '0px !important' }}>{body}</MobileBody>
                </MobileFilterDialog>
              </ThemeProvider>
            </EmotionCache>
          )
        )}
      </FocusLock>
    </>
  );
};

export default BookingPopup;
